import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { countryPhoneCodes } from "../../../../../data/country-phone-code";
import CustomSnackbar from "components/CustomSnackbar";
import DotPulseLoading from "components/Loading/DotPulseLoading";
import { useTranslation } from "react-i18next";
import { getmessage } from "utils/i18messages";
import { AuthContext } from "auth/AuthContext";

const ProfileForm = () => {
  const { user, updateUserData } = useContext(AuthContext);
  const [selectedCode, setSelectedCode] = useState(user?.userPhoneCode);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: user?.userFullName || "",
    country: user?.country || "",
    phone: user?.userPhone || "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Update formData jika user berubah
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.userFullName || "",
        country: user.country || "",
        phone: user.userPhone || "",
      });
      setSelectedCode(user.userPhoneCode);
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.country || !formData.phone) {
      setSnackbar({
        open: true,
        message: getmessage("profile_settings.password_required"),
        severity: "error",
      });
      return;
    }
    setLoading(true);

    try {
      // Kirim data ke backend - only changed fields
      const payload = {
        fullName: formData.name,
        country: formData.country,
        phone: formData.phone,
        phoneCode: selectedCode,
        ssoSetting: user?.isSsoLogin,
      };
      // Include profileImageUrl if user has one, to preserve it
      if (user?.profileImageUrl) {
        payload.profileImageUrl = user.profileImageUrl;
      }
      const lang = localStorage.getItem("i18nextLng") || "en";
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/auth/profile`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      // Update context with new data
      updateUserData({
        userFullName: formData.name,
        country: formData.country,
        userPhone: formData.phone,
        userPhoneCode: selectedCode,
      });

      setSnackbar({
        open: true,
        message: response.data.message || "Updated successfully.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    const country = countryPhoneCodes.find((c) => c.name === countryName);

    setFormData((prev) => ({
      ...prev,
      country: countryName,
    }));

    if (country) {
      setSelectedCode(country.code);
    } else {
      // Reset to current if no country selected
      setSelectedCode(user?.userPhoneCode);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mb: 2, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
        >
          {t("Dashboard_profile.personalInfo.title")}
        </Typography>
        <TextField
          fullWidth
          id="name-input"
          label={t("Dashboard_profile.personalInfo.fullName")}
          name="name"
          value={formData.name}
          onChange={handleChange}
          margin="normal"
          size="small"
        />

        <Autocomplete
          id="country"
          options={countryPhoneCodes}
          getOptionLabel={(option) => option.name}
          value={
            countryPhoneCodes.find((c) => c.name === formData.country) || null
          }
          onChange={(event, newValue) => {
            handleCountryChange({
              target: {
                name: "country",
                value: newValue ? newValue.name : "",
              },
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Dashboard_profile.personalInfo.country")}
              name="country"
              required
              margin="normal"
              size="small"
              fullWidth
            />
          )}
          isOptionEqualToValue={(option, value) => option.name === value.name}
        />

        <TextField
          id="phone-input"
          label={t("Dashboard_profile.personalInfo.phoneNumber")}
          name="phone"
          variant="outlined"
          type="number"
          fullWidth
          value={formData.phone}
          onChange={handleChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span style={{ marginLeft: 8 }}>{selectedCode}</span>
              </InputAdornment>
            ),
          }}
          required
          margin="normal"
          size="small"
        />

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            "& .MuiButton-root": {
              width: { xs: "100%", sm: "auto" },
            },
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            size="medium"
            disabled={loading}
          >
            {loading
              ? t("Dashboard_profile.actions.saving")
              : t("Dashboard_profile.actions.saveChanges")}
          </Button>
        </Box>
      </form>

      {/* Modern Snackbar for notifications */}
      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DotPulseLoading size={14} color="#3f51b5" />
        </Box>
      )}
    </>
  );
};

export default ProfileForm;
